import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
    Upload,
    FileText,
    Image,
    X,
    Loader2,
    CheckCircle2,
    AlertCircle,
    CloudUpload,
    ChevronDown,
    User,
    Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { user } = useAuth();
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState('');
    const [showContext, setShowContext] = useState(false);
    const [entitlement, setEntitlement] = useState(null);

    // Fetch the user's remaining report credits / plan entitlement
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await axios.get(`${API}/subscription/me`);
                if (active) setEntitlement(res.data);
            } catch (e) {
                // non-blocking
            }
        })();
        return () => { active = false; };
    }, [user]);
    
    // Patient context fields
    const [patientAge, setPatientAge] = useState('');
    const [patientGender, setPatientGender] = useState('');
    const [fastingStatus, setFastingStatus] = useState('');
    const [knownConditions, setKnownConditions] = useState('');

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const MAX_FILES = 10;

    const validateFile = (file) => {
        if (!allowedTypes.includes(file.type)) {
            return isRTL ? 'يُسمح فقط بملفات PDF أو JPG أو PNG' : 'Please upload a PDF, JPG, or PNG file';
        }
        if (file.size > maxSize) {
            return isRTL ? 'حجم الملف يجب أن يكون أقل من 10 ميجابايت' : 'File size must be less than 10MB';
        }
        return null;
    };

    const addFiles = (fileList) => {
        setError('');
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        if (files.length + incoming.length > MAX_FILES) {
            setError(isRTL ? `الحد الأقصى ${MAX_FILES} ملفات في التحليل الواحد` : `Maximum ${MAX_FILES} files per analysis`);
            return;
        }
        const next = [];
        for (const f of incoming) {
            const validationError = validateFile(f);
            if (validationError) {
                setError(validationError);
                return;
            }
            next.push({ file: f, preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null });
        }
        setFiles((prev) => [...prev, ...next]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleInputChange = (e) => {
        addFiles(e.target.files);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setFiles((prev) => {
            const item = prev[index];
            if (item?.preview) URL.revokeObjectURL(item.preview);
            return prev.filter((_, i) => i !== index);
        });
        setError('');
    };

    const handleUpload = async () => {
        if (!files.length) return;

        setUploading(true);
        setProgress(0);

        const formData = new FormData();
        files.forEach(({ file }) => formData.append('files', file));
        
        // Add language preference for AI content generation
        formData.append('language', i18n.language);
        
        // Add patient context if provided
        if (patientAge) formData.append('patient_age', patientAge);
        if (patientGender) formData.append('patient_gender', patientGender);
        if (fastingStatus) formData.append('fasting_status', fastingStatus);
        if (knownConditions) formData.append('known_conditions', knownConditions);

        try {
            // Simulate progress
            const progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) return prev;
                    return prev + 10;
                });
            }, 500);

            const response = await axios.post(`${API}/reports/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const reportId = response.data.id;
            let reportStatus = response.data.status || 'completed';

            // Poll until the background AI analysis completes
            while (reportStatus === 'processing') {
                await new Promise((r) => setTimeout(r, 4000));
                const check = await axios.get(`${API}/reports/${reportId}`);
                reportStatus = check.data.status || 'completed';
            }

            clearInterval(progressInterval);

            if (reportStatus === 'failed') {
                const failMsg = i18n.language === 'ar'
                    ? 'تعذر تحليل التقرير — لم يتم خصم أي رصيد، حاول مرة أخرى'
                    : 'Analysis failed — no credit was used, please try again';
                setError(failMsg);
                toast.error(failMsg);
                return;
            }

            setProgress(100);

            if (response.data.duplicate) {
                toast.info(i18n.language === 'ar'
                    ? 'هذا الملف تم تحليله مسبقاً — تم فتح تقريرك الجاهز دون خصم رصيد إضافي'
                    : 'This file was already analyzed — opening your existing report (no extra credit used)');
            } else {
                toast.success(i18n.language === 'ar' ? 'تم تحليل تقريرك بنجاح!' : 'Report analyzed successfully!');
            }
            
            // Navigate to report page after short delay
            setTimeout(() => {
                navigate(`/report/${reportId}`);
            }, 500);

        } catch (err) {
            console.error('Upload error:', err);
            const status = err.response?.status;
            const message = err.response?.data?.detail || 'Failed to upload report. Please try again.';
            setError(message);
            // No plan / no credits -> guide the user to the subscription page
            if (status === 402 || status === 403) {
                toast.error(message, {
                    action: {
                        label: i18n.language === 'ar' ? 'الاشتراك' : 'Subscribe',
                        onClick: () => navigate('/subscription'),
                    },
                });
            } else {
                toast.error(message);
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="upload-page">
            <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-stone-800 mb-2">{t('upload.title')}</h1>
                <p className="text-stone-500">{t('upload.subtitle')}</p>
            </div>

            {/* Report credits / entitlement banner */}
            {entitlement && (
                entitlement.unlimited_reports ? (
                    <div className="mb-6 flex items-center justify-center gap-2 p-3 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 text-sm" data-testid="credits-banner">
                        <CheckCircle2 className="w-4 h-4" />
                        {isRTL ? 'باقتك تتيح تحاليل غير محدودة' : 'Your plan includes unlimited analyses'}
                    </div>
                ) : (entitlement.report_credits > 0 ? (
                    <div className="mb-6 flex items-center justify-center gap-2 p-3 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 text-sm" data-testid="credits-banner">
                        <FileText className="w-4 h-4" />
                        {isRTL
                            ? `رصيدك المتبقي: ${entitlement.report_credits} تحليل`
                            : `Remaining credits: ${entitlement.report_credits} analyses`}
                    </div>
                ) : (
                    <div className="mb-6 flex flex-col sm:flex-row items-center justify-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="credits-banner-empty">
                        <span className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {isRTL ? 'لا يوجد رصيد تحاليل متاح' : 'No analysis credits available'}
                        </span>
                        <Link to="/subscription">
                            <Button size="sm" className="bg-teal-500 hover:bg-teal-600 text-white rounded-full" data-testid="banner-subscribe-btn">
                                {isRTL ? 'اشترك الآن' : 'Subscribe now'}
                            </Button>
                        </Link>
                    </div>
                ))
            )}

            <Card className="border-0 shadow-xl shadow-stone-200/50">
                <CardHeader>
                    <CardTitle className="text-lg">{isRTL ? 'اختر الملف' : 'Select File'}</CardTitle>
                    <CardDescription>
                        {t('upload.supportedFormats')} ({t('upload.maxSize')})
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="upload-error">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            className={`upload-zone ${files.length ? 'p-6' : 'p-12'} text-center ${dragOver ? 'drag-over' : ''}`}
                            data-testid="upload-dropzone"
                        >
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleInputChange}
                                className="hidden"
                                id="file-input"
                                data-testid="file-input"
                                disabled={files.length >= MAX_FILES}
                            />
                            <label htmlFor="file-input" className="cursor-pointer block">
                                {files.length === 0 ? (
                                    <>
                                        <div className="w-20 h-20 rounded-3xl bg-teal-100 flex items-center justify-center mx-auto mb-6">
                                            <CloudUpload className="w-10 h-10 text-teal-600" />
                                        </div>
                                        <p className="text-lg font-medium text-stone-700 mb-2">
                                            {t('upload.dragDrop')}
                                        </p>
                                        <p className="text-stone-500 mb-1">{isRTL ? 'أو انقر لاختيار الملفات' : 'or click to browse files'}</p>
                                        <p className="text-xs text-teal-600 mb-4">
                                            {isRTL
                                                ? 'يمكنك رفع حتى 10 ملفات (صفحات التقرير) — تُحلّل معاً كتقرير واحد'
                                                : 'Upload up to 10 files (report pages) — analyzed together as one report'}
                                        </p>
                                        <Button variant="outline" className="rounded-full px-6 pointer-events-none">
                                            {t('upload.browse')}
                                        </Button>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center gap-2 text-teal-700" data-testid="add-more-files">
                                        <CloudUpload className="w-5 h-5" />
                                        <span className="text-sm font-medium">
                                            {files.length >= MAX_FILES
                                                ? (isRTL ? 'وصلت للحد الأقصى (10 ملفات)' : 'Maximum reached (10 files)')
                                                : (isRTL ? `أضف المزيد من الصفحات (${files.length}/10)` : `Add more pages (${files.length}/10)`)}
                                        </span>
                                    </div>
                                )}
                            </label>
                        </div>

                        {files.length > 0 && (
                            <div className="space-y-2" data-testid="files-queue">
                                {files.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl border border-stone-100" data-testid={`file-item-${idx}`}>
                                        {item.preview ? (
                                            <img src={item.preview} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                                                <FileText className="w-6 h-6 text-teal-600" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-stone-800 text-sm truncate">{item.file.name}</p>
                                            <p className="text-xs text-stone-500 ltr-number">
                                                {(item.file.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeFile(idx)}
                                            disabled={uploading}
                                            className="text-stone-400 hover:text-red-600 shrink-0"
                                            data-testid={`remove-file-btn-${idx}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                                <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    {isRTL
                                        ? `${files.length} ملف جاهز — سيتم تحليلها معاً كتقرير واحد (خصم رصيد واحد فقط)`
                                        : `${files.length} file(s) ready — analyzed together as one report (1 credit)`}
                                </div>
                            </div>
                        )}

                        <div className={files.length ? 'space-y-6' : 'hidden'}>
                            {/* Patient Context (Optional) */}
                            <Collapsible open={showContext} onOpenChange={setShowContext}>
                                <CollapsibleTrigger asChild>
                                    <Button variant="ghost" className="w-full justify-between text-stone-600 hover:text-stone-800">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            <span>{t('upload.patientInfo')}</span>
                                            <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">{isRTL ? 'يحسن الدقة' : 'Improves accuracy'}</span>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 transition-transform ${showContext ? 'rotate-180' : ''}`} />
                                    </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pt-4">
                                    <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 space-y-4">
                                        <div className="flex items-start gap-2 text-xs text-blue-700">
                                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                            <p>{isRTL ? 'إضافة السياق يساعد الذكاء الاصطناعي على تقديم تفسيرات أكثر دقة وتخصيصاً. جميع الحقول اختيارية.' : 'Adding context helps the AI provide more personalized and accurate interpretations. All fields are optional.'}</p>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-stone-600">{t('upload.age')}</Label>
                                                <Input
                                                    type="number"
                                                    placeholder={isRTL ? 'مثال: 35' : 'e.g., 35'}
                                                    value={patientAge}
                                                    onChange={(e) => setPatientAge(e.target.value)}
                                                    className="bg-white"
                                                    data-testid="patient-age-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-stone-600">{t('upload.gender')}</Label>
                                                <Select value={patientGender} onValueChange={setPatientGender}>
                                                    <SelectTrigger className="bg-white" data-testid="patient-gender-select">
                                                        <SelectValue placeholder={isRTL ? 'اختر الجنس' : 'Select gender'} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="male">{t('upload.male')}</SelectItem>
                                                        <SelectItem value="female">{t('upload.female')}</SelectItem>
                                                        <SelectItem value="other">{isRTL ? 'آخر' : 'Other'}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-stone-600">{t('upload.fasting')}</Label>
                                            <Select value={fastingStatus} onValueChange={setFastingStatus}>
                                                <SelectTrigger className="bg-white" data-testid="fasting-status-select">
                                                    <SelectValue placeholder={isRTL ? 'هل كنت صائماً؟' : 'Were you fasting?'} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="fasting">{isRTL ? 'نعم، صائم (8+ ساعات)' : 'Yes, fasting (8+ hours)'}</SelectItem>
                                                    <SelectItem value="non_fasting">{isRTL ? 'لا، غير صائم' : 'No, not fasting'}</SelectItem>
                                                    <SelectItem value="unknown">{isRTL ? 'غير متأكد' : 'Not sure'}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-stone-600">{t('upload.conditions')} ({t('common.optional')})</Label>
                                            <Input
                                                placeholder={t('upload.conditionsPlaceholder')}
                                                value={knownConditions}
                                                onChange={(e) => setKnownConditions(e.target.value)}
                                                className="bg-white"
                                                data-testid="known-conditions-input"
                                            />
                                            <p className="text-xs text-stone-500">{isRTL ? 'افصل بين الحالات المتعددة بفواصل' : 'Separate multiple conditions with commas'}</p>
                                        </div>
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>

                            {/* Progress */}
                            {uploading && (
                                <div className="space-y-2" data-testid="upload-progress">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-stone-600">{t('upload.analyzing')}</span>
                                        <span className="font-medium text-teal-600 ltr-number">{progress}%</span>
                                    </div>
                                    <Progress value={progress} className="h-2" />
                                    <p className="text-xs text-stone-500 text-center">
                                        {isRTL ? 'الذكاء الاصطناعي يقوم بالتحليل الشامل وإنشاء رؤى مخصصة' : 'Our AI is performing cross-analysis and generating personalized insights'}
                                    </p>
                                </div>
                            )}

                            {/* Upload Button */}
                            <Button
                                onClick={handleUpload}
                                disabled={uploading}
                                className="w-full h-14 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium text-lg shadow-lg shadow-teal-500/25 transition-all hover:scale-[1.01]"
                                data-testid="analyze-btn"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="ltr:mr-2 rtl:ml-2 w-5 h-5 animate-spin" />
                                        {isRTL ? 'جاري التحليل...' : 'Analyzing Report...'}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="ltr:mr-2 rtl:ml-2 w-5 h-5" />
                                        {isRTL ? 'تحليل التقرير' : 'Analyze Report'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Features */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                    { title: isRTL ? 'تحليل شامل' : 'Cross-Analysis', desc: isRTL ? 'الذكاء الاصطناعي يربط التحاليل لاكتشاف الأنماط' : 'AI connects related tests to find patterns' },
                    { title: isRTL ? 'تصنيف الشدة' : 'Severity Classification', desc: isRTL ? 'فهم مدى انحراف القيم' : 'Understand how far values deviate' },
                    { title: isRTL ? 'مقارنة المؤشرات' : 'Trend Comparison', desc: isRTL ? 'تتبع التغييرات عبر تقارير متعددة' : 'Track changes across multiple reports' },
                    { title: isRTL ? 'شرح مزدوج' : 'Dual Explanations', desc: isRTL ? 'لغة طبية + مبسطة للجميع' : 'Medical + simple language for everyone' }
                ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-teal-50/50 rounded-xl border border-teal-100">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-stone-700 text-sm">{feature.title}</p>
                            <p className="text-xs text-stone-500">{feature.desc}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tips */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                    { icon: FileText, title: 'Clear Scans', desc: 'Ensure text is readable' },
                    { icon: Image, title: 'Good Lighting', desc: 'Avoid shadows on images' },
                    { icon: CheckCircle2, title: 'Complete Pages', desc: 'Include all test results' }
                ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl">
                        <tip.icon className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-stone-700 text-sm">{tip.title}</p>
                            <p className="text-xs text-stone-500">{tip.desc}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 bg-stone-100 rounded-2xl">
                <p className="text-sm text-stone-600 text-center">
                    🔒 Your data is processed securely and never shared. Reports are encrypted and stored only with your consent.
                </p>
            </div>
        </div>
    );
};

export default UploadPage;
